import { useEffect } from 'react'

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

/** How long to watch for the row before giving up (a bad/stale uid in the
 * deep link, or a row the reader can't see, must not watch forever). */
const GIVE_UP_MS = 15000

/**
 * The return half of the document-highlight-click round trip (user,
 * 2026-09-16): a `from=asset:<uid>:<propertyUid>` / `contract:<uid>:<code>`
 * deep link lands on the asset properties tab or the contract clauses tab,
 * and this scrolls the matching row (`data-testid="<testId>"`) into view and
 * marks it with `row-highlight-pulse` (src/index.css) — the same 0.9s × 3
 * cadence as the viewer's own `document-highlight-pulse`, so the two ends of
 * the gesture read as one animation vocabulary.
 *
 * `testId` is undefined whenever the deep link carried no row-level origin
 * (an entity-only `from`, or none at all) — nothing scrolls or pulses then,
 * same as before this existed.
 *
 * The row is almost never on the page yet when this effect first runs — it
 * renders once the properties/clauses query resolves, same as any other
 * fetched list — so this watches for it (MutationObserver, bounded by
 * GIVE_UP_MS) rather than assuming a fixed load time. Once found, the scroll
 * is deferred two animation frames, mirroring DocumentPageHighlight's own
 * centerInWindow: the router's own scroll restoration can still be running
 * this soon after navigation and would undo a synchronous scroll.
 */
export function useFocusAndPulseRow(testId: string | undefined | null): void {
  useEffect(() => {
    if (!testId) return
    let cancelled = false
    let raf1 = 0
    let raf2 = 0
    const selector = `[data-testid="${CSS.escape(testId)}"]`

    const activate = (el: Element) => {
      raf1 = requestAnimationFrame(() => {
        raf2 = requestAnimationFrame(() => {
          if (cancelled) return
          el.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'center' })
          el.classList.add('row-highlight-pulse')
          const clear = () => el.classList.remove('row-highlight-pulse')
          el.addEventListener('animationend', clear, { once: true })
        })
      })
    }

    const existing = document.querySelector(selector)
    if (existing) {
      activate(existing)
      return () => {
        cancelled = true
        cancelAnimationFrame(raf1)
        cancelAnimationFrame(raf2)
      }
    }

    const observer = new MutationObserver(() => {
      const el = document.querySelector(selector)
      if (el) {
        observer.disconnect()
        activate(el)
      }
    })
    observer.observe(document.body, { childList: true, subtree: true })
    const giveUp = window.setTimeout(() => observer.disconnect(), GIVE_UP_MS)

    return () => {
      cancelled = true
      observer.disconnect()
      window.clearTimeout(giveUp)
      cancelAnimationFrame(raf1)
      cancelAnimationFrame(raf2)
    }
  }, [testId])
}
